import { isEntityLoading } from '@/Models/Entity';
import { type WorklistFilterParams, type WorklistViewParams } from '@/Models/GenericWorklist/WorklistParams';
import { makeAutoObservable } from 'mobx';
import { ICustomWorklistHelper } from '../../Stores/CustomWorklistHelper';
import { type IWorklistStore } from '../../Stores/WorklistStore';
import { WorklistFilterProperty } from '../../Logic/WorklistProperties';
import { worklistHasMinimumCriteria } from '../../Logic/WorklistFilterCriteriaLogic';
import { clearHiddenFilterFields } from '../../Logic/WorklistFilterPropertyMapping';

export interface IWorklistSidebarStore {
    activeWorklistFilter?: WorklistFilterParams;
    currentFilter?: WorklistFilterParams;
    isCurrentFilterIdenticalToActiveFilter: boolean;
    isCurrentFilterValid: boolean;
    showMinimumCriteriaAlert: boolean;
    isRestoreDisabled: boolean;
    worklistStore: IWorklistStore;
    applyCurrentFilter: () => void;
    setCurrentFilter: (filterParams: WorklistFilterParams | undefined) => void;
    toggleSaveWorklistDialog: (open: boolean) => void;
    toggleUpdateWorklistDialog: (open: boolean) => void;
    toggleDeleteModal: (open?: boolean) => void;
    toggleSidebarCollapsed: () => Promise<void>;
    isSidebarCollapsed: boolean;
}

export default class WorklistSidebarStore implements IWorklistSidebarStore {
    currentFilter?: WorklistFilterParams;
    deleteModalIsOpen = false;
    saveWorklistModalIsOpen = false;
    selectedSpecificFilter?: WorklistFilterProperty;
    updateWorklistModalIsOpen = false;
    isEmptyWorklistSidebarCollapsed = false;
    hasAttemptedInvalidApply = false;

    readonly customWorklistStore: ICustomWorklistHelper;
    readonly worklistStore: IWorklistStore;

    constructor(
        customWorklistStore: ICustomWorklistHelper,
        worklistStore: IWorklistStore,
    ) {
        this.customWorklistStore = customWorklistStore;
        this.worklistStore = worklistStore;

        makeAutoObservable(this);
        this.currentFilter = worklistStore.worklist.Params.Filter;
    }

    get isSidebarCollapsed(): boolean {
        const selected = this.customWorklistStore.currentlySelectedCustomWorklist;
        if (selected !== null) {
            return selected.UserConfig?.IsSidebarCollapsed ?? false;
        }
        return this.isEmptyWorklistSidebarCollapsed;
    }

    get activeWorklistFilter(): WorklistFilterParams | undefined {
        return this.worklistStore.worklist.Params.Filter;
    }

    get isLoading(): boolean {
        return isEntityLoading(this.worklistStore.results);
    }

    get isRestoreDisabled(): boolean {
        return !(!this.isLoading && this.customWorklistStore.isCustomWorklistSelected);
    }

    get isCurrentFilterIdenticalToActiveFilter(): boolean {
        return this.currentFilter == this.activeWorklistFilter;
    }

    get isCurrentFilterValid(): boolean {
        return worklistHasMinimumCriteria(this.currentFilter);
    }

    get showMinimumCriteriaAlert(): boolean {
        return this.hasAttemptedInvalidApply && !this.isCurrentFilterValid;
    }

    get worklistViewConfig(): WorklistViewParams {
        return this.worklistStore.worklist.View;
    }

    setCurrentFilter = (filterParams: WorklistFilterParams | undefined): void => {
        this.currentFilter = filterParams;

        if (this.isCurrentFilterValid) {
            this.hasAttemptedInvalidApply = false;
        }
    };

    applyCurrentFilter(): void {
        if (!this.isCurrentFilterValid) {
            this.hasAttemptedInvalidApply = true;
            return;
        }

        this.hasAttemptedInvalidApply = false;
        const hiddenProperties = this.worklistViewConfig.Filters
            ?.filter((f) => !f.Visible)
            .map((f) => f.ID) ?? [];
        const cleanedFilter = clearHiddenFilterFields(this.currentFilter, hiddenProperties);
        this.currentFilter = cleanedFilter;
        this.worklistStore.applyFilter(cleanedFilter);
    }

    async deleteActiveWorklist(): Promise<void> {
        await this.customWorklistStore.deleteCurrentSelectedCustomWorklist();
        this.deleteModalIsOpen = false;
    }

    toggleDeleteModal = (open?: boolean): void => {
        if (!this.customWorklistStore.currentlySelectedCustomWorklist) {
            return console.warn(`Can't delete since no saved filter is selected`);
        }

        this.deleteModalIsOpen = open ?? !this.deleteModalIsOpen;
    };

    toggleSaveWorklistDialog = (open: boolean): void => {
        this.saveWorklistModalIsOpen = open;
    };

    toggleUpdateWorklistDialog = (open: boolean): void => {
        this.updateWorklistModalIsOpen = open;
    };

    toggleSidebarCollapsed = async (): Promise<void> => {
        if (!this.customWorklistStore.isCustomWorklistSelected) {
            this.isEmptyWorklistSidebarCollapsed = !this.isEmptyWorklistSidebarCollapsed;
            return;
        }
        return this.customWorklistStore.toggleSidebarCollapsedState();
    };

    setSpecificFilter = (filter?: WorklistFilterProperty): void => {
        this.selectedSpecificFilter = filter;
    };
}
